import { findPlanDisplayedName, useCmsTherapyPlans } from "@/infra/hooks/useCmsTherapyPlans";
import { useIntl } from "@/infra/locale";
import { useTreatmentSelectionStore } from "@/infra/stores/treatmentSelectionStore";
import { webshopEnvironment } from "@akina/config";
import { Text } from "@akina/presentation";

function StepBadge({ number }: Readonly<{ number: number }>) {
  return (
    <div className="bg-primary-100 mt-1 flex size-6 shrink-0 items-center justify-center rounded-full">
      <span className="font-area text-sm leading-none font-extrabold text-white">{number}</span>
    </div>
  );
}

type StepItemProps = { number: number; title: string; description: string };

export function CheckoutRecap() {
  return (
    <aside className="bg-primary-50 flex w-full shrink-0 flex-col gap-8 rounded-2xl p-6 lg:w-106.75">
      <ProductCard />
      <StepList />
    </aside>
  );
}

function ProductCard() {
  const { formatMessage } = useIntl();
  const selectedTemplateKey = useTreatmentSelectionStore(s => s.selectedTemplateKey);
  const { plans } = useCmsTherapyPlans();
  const planDisplayedName = findPlanDisplayedName(plans, selectedTemplateKey);
  const explanationWords = formatMessage({ id: "checkoutPage.paymentStep.productCard.designedFor3MonthsExplanation" }).split(" ");
  const cancelFaqPath = formatMessage({ id: "checkoutPage.paymentStep.productCard.cancelSectionInFaqUrl" });
  const cancelFaqUrl = cancelFaqPath ? new URL(cancelFaqPath, webshopEnvironment.websiteUrl).href : undefined;

  return (
    <div className="flex w-full flex-col gap-2 rounded-2xl bg-white p-4 lg:w-96">
      <div className="flex h-12.5 items-start justify-between">
        <Text size="lg" className="leading-tight" fontWeight="font-extrabold" color="text-gray-800">
          {selectedTemplateKey === "t_Universal"
            ? formatMessage({ id: "checkoutPage.paymentStep.productCard.productNameCustom" })
            : formatMessage(
                { id: "checkoutPage.paymentStep.productCard.productName" },
                { therapyPlan: planDisplayedName },
              )}
        </Text>
        <div className="flex min-w-[70px] flex-col items-end leading-tight">
          <Text size="lg" className="font-extrabold text-gray-800">
            {formatMessage({ id: "checkoutPage.paymentStep.productCard.price" })}
          </Text>
          <Text size="xs" className="tracking-wide" color="text-black">
            {formatMessage({ id: "checkoutPage.paymentStep.productCard.perMonth" })}
          </Text>
        </div>
      </div>

      <Text size="sm" color="text-gray-800" className="max-w-68">
        {explanationWords.slice(0, -2).join(" ")}{" "}
        {cancelFaqUrl
          ? <a href={cancelFaqUrl} target="_blank" rel="noreferrer" className="text-primary-100">{explanationWords.slice(-2).join(" ")}</a>
          : <span className="text-primary-100">{explanationWords.slice(-2).join(" ")}</span>}
      </Text>

      <div className="flex items-baseline justify-between">
        <Text color="text-gray-800" fontWeight="font-medium">
          {formatMessage({ id: "checkoutPage.paymentStep.productCard.codeApplied" })}
        </Text>
        <Text color="text-secondary-500" fontWeight="font-medium">
          {formatMessage({ id: "checkoutPage.paymentStep.productCard.savedAmount" })}
        </Text>
      </div>

      <hr className="my-2 h-0.5 border-gray-800/10" />

      <div className="flex items-baseline justify-between">
        <p className="font-area text-lg font-extrabold text-gray-800">
          {formatMessage({ id: "checkoutPage.paymentStep.productCard.totalTodayLabel" })}
        </p>
        <p className="font-area text-lg font-extrabold text-gray-800">
          {formatMessage({ id: "checkoutPage.paymentStep.productCard.totalPrice" })}
        </p>
      </div>

      <p className="font-area text-sm text-gray-800">
        {formatMessage({ id: "checkoutPage.paymentStep.productCard.youSaveThisMuchExplanation" })}
      </p>
    </div>
  );
}

function StepList() {
  const { formatMessage } = useIntl();

  return (
    <div className="flex w-full flex-col gap-4 lg:w-96">
      <StepItem
        number={1}
        title={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.detailsTitle" })}
        description={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.detailsDescription" })}
      />
      <StepItem
        number={2}
        title={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.downloadTitle" })}
        description={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.downloadDescription" })}
      />
      <StepItem
        number={3}
        title={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.startTitle" })}
        description={formatMessage({ id: "checkoutPage.paymentStep.productCard.steps.startDescription" })}
      />
    </div>
  );
}

function StepItem({ number, title, description }: Readonly<StepItemProps>) {
  return (
    <div className="flex gap-2">
      <StepBadge number={number} />
      <div>
        <Text size="lg" fontWeight="font-extrabold" color="text-gray-800/70">
          {title}
        </Text>
        <Text size="sm" color="text-gray-800/70">
          {description}
        </Text>
      </div>
    </div>
  );
}
